/**
 * Created by miguel on 23/2/17.
 */

/**
 * Info tab of an event: the event itself (name, start/end, active, pax, total, description) and
 * its configuration for the online pages (booking configuration with its business area and days,
 * product preset). The phases and lines live in the Planning tab (EventDetailPlanningViewController),
 * the price and capacity in the Tickets tab, the cover image in the Web tab.
 */
class EventDetailInfoViewController extends MUIViewController
{
    private nameLabel:MUILabel         = null;
    private paxTextField:MUITextField  = null;
    private dateTextField:MUITextField = null;
    private calendarButton:MUIButton   = null;
    private endDateTextField:MUITextField   = null;
    private endCalendarButton:MUIButton     = null;
    private activeButton:MUISwitchButton    = null;
    private kindComboBox:MUIComboBox        = null;
    private bookingConfigButton:MUIButton   = null;
    private businessAreaLabel:MUILabel      = null;
    private weekdaysLabel:MUILabel          = null;
    private presetComboBox:MUIComboBox      = null;
    private publicPagesLabel:MUILabel       = null;
    private totalLabel:MUILabel        = null;
    private registrationInfoTextArea:MUITextArea = null;
    private registrationLanguageButton:MUIButton = null;

    private df:MIODateFormatter = MUIWebApplication.sharedInstance().delegate.dateFormatter;
    private tf:MIODateFormatter = MUIWebApplication.sharedInstance().delegate.timeFormatter;
    private cf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.currencyFormatter;

    viewDidLoad(){
        super.viewDidLoad();

        this.nameLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        // Start / end are date-times ("DD/MM/YYYY HH:MM"); the calendar button changes the day only
        // Typed values are applied as soon as they parse; an invalid value is only reported (and
        // reverted) when the field is left, so typing doesn't trigger alerts on every keystroke.
        this.dateTextField = MUIOutlet(this, 'date-tf','MUITextField');
        this.dateTextField.setOnChangeText(this, function(this:EventDetailInfoViewController, textField:MUITextField, value:string) {
            let date = this.parseDateTime(value);
            if (date != null) this.setBeginDateTime(date, false);
        });
        this.dateTextField.setOnDidEndEditing(this, function(this:EventDetailInfoViewController, textField:MUITextField) {
            this.validateDateTimeField(textField, this._event?.beginDateTime);
        });

        this.calendarButton = MUIOutlet(this, 'calendar-btn','MUIButton');
        this.calendarButton.setAction(this, function(this:EventDetailInfoViewController){
            this.showDatePicker(this.calendarButton, "begin");
        });

        this.endDateTextField = MUIOutlet(this, 'end-date-tf','MUITextField');
        this.endDateTextField.setOnChangeText(this, function(this:EventDetailInfoViewController, textField:MUITextField, value:string) {
            let date = this.parseDateTime(value);
            if (date != null) this._event.endDateTime = date;
        });
        this.endDateTextField.setOnDidEndEditing(this, function(this:EventDetailInfoViewController, textField:MUITextField) {
            this.validateDateTimeField(textField, this._event?.endDateTime);
        });

        this.endCalendarButton = MUIOutlet(this, 'end-calendar-btn','MUIButton');
        this.endCalendarButton.setAction(this, function(this:EventDetailInfoViewController){
            this.showDatePicker(this.endCalendarButton, "end");
        });

        // What the event is (show, planned, other): decides the sections and the public pages it
        // appears in; an event can change kind at any time
        this.kindComboBox = MUIOutlet(this, 'kind-cb', 'MUIComboBox');
        this.kindComboBox.setOnChangeAction(this, function(this:EventDetailInfoViewController, control:MUIComboBox, value:string){
            this._event.kind = parseInt(value) || VenueEventKind.Generic;
        });

        this.activeButton = MUIOutlet(this, 'active-btn', 'MUISwitchButton');
        this.activeButton.setOnChangeValue(this, function(this:EventDetailInfoViewController, control, value:boolean){
            this._event.isActive = value;
        });

        this.paxTextField = MUIOutlet(this, 'pax-tf','MUITextField');
        this.paxTextField.setOnChangeText(this, function(this:EventDetailInfoViewController, textField:MUITextField, value:string) {
            this._event.pax = parseInt(value) || 0;
        });

        this.totalLabel = MUIOutlet(this, 'total-lbl', 'MUILabel');

        // Why the guest registers, shown by the online events page. Localized like the
        // product name: the language button opens the per-language values of this attribute.
        this.registrationInfoTextArea = MUIOutlet(this, 'registration-info-ta', 'MUITextArea');
        this.registrationInfoTextArea.setOnChangeText(this, function(this:EventDetailInfoViewController, control:MUITextArea, value:string){
            this._event.registrationInfo = value.length > 0 ? value.trim() : null;
        });

        this.registrationLanguageButton = MUIOutlet(this, 'registration-language-btn', 'MUIButton');
        this.registrationLanguageButton.setAction(this, function(this:EventDetailInfoViewController){
            let vc = ProductLanguageViewController.newInstance();
            vc.setItem(this._event, "registrationInfo", true);
            vc.modalSize = new MIOSize(800, 400);
            this.presentViewController(vc, true);
        });

        // The event's booking configuration (days, hours, business area) without hunting through
        // Online settings: opens the same dialog, pre-linked to this event.
        this.bookingConfigButton = MUIOutlet(this, 'booking-config-btn', 'MUIButton');
        this.bookingConfigButton.setAction(this, function(this:EventDetailInfoViewController){
            this.showBookingConfiguration();
        });

        this.businessAreaLabel = MUIOutlet(this, 'business-area-lbl', 'MUILabel');
        this.weekdaysLabel = MUIOutlet(this, 'weekdays-lbl', 'MUILabel');
        this.publicPagesLabel = MUIOutlet(this, 'public-pages-lbl', 'MUILabel');

        // The product preset the online booking page lists for this event (VenueEvent.presetGroupID)
        this.presetComboBox = MUIOutlet(this, 'preset-cb', 'MUIComboBox');
        this.presetComboBox.setOnChangeAction(this, function(this:EventDetailInfoViewController, control:MUIComboBox, value:string){
            this._event.presetGroupID = (value != null && value != "-1") ? value : null;
        });
        this.loadPresets();
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _event:VenueEvent = null;
    set event(value:VenueEvent){
        this._event = value;
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false || this._event == null) return;

        this.nameLabel.text = this._event.name;
        this.updateDateUI();
        this.activeButton.on = this._event.isActive == true;
        this.kindComboBox.selectItem(String(this._event.kind ?? VenueEventKind.Generic));
        this.paxTextField.text = this._event.pax;
        this.registrationInfoTextArea.text = this._event.registrationInfo;
        this.totalLabel.text = this.cf.stringFromNumber(this._event.totalAmount || 0);
        this.presetComboBox.selectItem(this._event.presetGroupID ?? "-1");
        this.updateBookingConfigUI();
        this.updatePublicPagesUI();
    }

    // --- Presets ---

    private loadPresets(){
        DBHelper.queryObjectsWithCompletion("Preset", [MIOSortDescriptor.sortDescriptorWithKey("name", true)], MIOPredicate.predicateWithFormat("deletedAt = null"), [], this, function(this:EventDetailInfoViewController, objects:Preset[]){
            this.presetComboBox.removeAllItems();
            this.presetComboBox.addItem(MIOLocalizeString("NO_PRESET", "No preset"), "-1");
            for (let p of objects) this.presetComboBox.addItem(p.name, p.identifier);
            if (this._event != null) this.presetComboBox.selectItem(this._event.presetGroupID ?? "-1");
        });
    }

    // --- Booking configuration ---

    private updateBookingConfigUI(){
        if (this.bookingConfigButton == null || this._event == null) return;
        let vc = this;
        this.fetchBookingConfiguration(function(config:BookingTimeRangePresetConfiguration){
            vc.bookingConfigButton.title = config != null ? config.name : MIOLocalizeString("SET_UP", "SET UP");
            vc.businessAreaLabel.text = config?.businessArea?.name ?? MIOLocalizeString("NOT_CONFIGURED", "Not configured");
            vc.weekdaysLabel.text = config != null ? EventDetailInfoViewController.weekdaysText(config.weekdays) : "";
        });
    }

    // "Mon, Tue, Fri" from the WeekDays bitmask of the configuration (127 = every day)
    static weekdaysText(weekdays:number):string {
        if (weekdays == null || weekdays >= 127) return MIOLocalizeString("EVERY_DAY", "Every day");
        let days = [ [WeekDays.Monday, "MON"], [WeekDays.Tuesdays, "TUE"], [WeekDays.Wednesday, "WED"], [WeekDays.Thursday, "THU"], [WeekDays.Friday, "FRI"], [WeekDays.Saturday, "SAT"], [WeekDays.Sunday, "SUN"] ];
        let names = [];
        for (let [bit, key] of days) if ((weekdays & (bit as number)) != 0) names.push(MIOLocalizeString(key as string, key as string));
        return names.length > 0 ? names.join(", ") : MIOLocalizeString("NO_DAYS", "No days");
    }

    private fetchBookingConfiguration(completion:any){
        let predicate = "event.identifier == '" + this._event.identifier + "' AND deletedAt == null";
        DBHelper.queryObjectsWithCompletion("BookingTimeRangePresetConfiguration", null, MIOPredicate.predicateWithFormat(predicate), ["timeRangePreset", "businessArea", "event"], this, function(objects){
            completion(objects.length > 0 ? objects[0] as BookingTimeRangePresetConfiguration : null);
        });
    }

    private showBookingConfiguration(){
        let vc = this;
        this.fetchBookingConfiguration(function(config:BookingTimeRangePresetConfiguration){
            let dialog = new AddOnlineConfigurationsViewController('add-online-configuration-view');
            dialog.initWithResource('layout/onlinesettings/AddOnlineConfigurationView.html');
            dialog.configurationType = TimeRangePresetConfigurationType.Booking;
            if (config != null) dialog.item = config;
            else dialog.presetForEvent(vc._event);
            AppHelper.sharedInstance().presentViewController(dialog, true);
        });
    }

    // Where the event is offered online: the booking page lists it while it is active and not
    // finished; the events page also needs places left (see the Tickets tab)
    private updatePublicPagesUI(){
        let parts = [];
        parts.push(MIOLocalizeString("ONLINE_BOOKING_PAGE", "Online booking page") + ": " + (this._event.isActive ? MIOLocalizeString("LISTED", "listed") : MIOLocalizeString("NOT_LISTED", "not listed")));
        parts.push(MIOLocalizeString("ONLINE_EVENTS_PAGE", "Online events page") + ": " + (this._event.isActive ? MIOLocalizeString("LISTED", "listed") : MIOLocalizeString("NOT_LISTED", "not listed")));
        this.publicPagesLabel.text = parts.join(" · ");
    }

    // --- Dates ---

    private updateDateUI(){
        let begin = this._event.beginDateTime, end = this._event.endDateTime;
        this.dateTextField.text    = this.dateTimeString(begin);
        this.endDateTextField.text = this.dateTimeString(end);
    }

    private dateTimeString(date:Date):string {
        return date != null ? this.df.stringFromDate(date) + " " + this.tf.stringFromDate(date) : "";
    }

    // "DD/MM/YYYY HH:MM" (date part in the app date format, time optional = 00:00) -> Date or null
    private parseDateTime(value:string):Date {
        let m = /^\s*(\S+)(?:\s+(\d{1,2}[:.hH]\d{2}))?\s*$/.exec(value ?? "");
        if (m == null) return null;
        let day = this.df.dateFromString(m[1]);
        if (day == null) return null;
        let time = m[2] != null ? EventDetailInfoViewController.parseTime(m[2]) : [0, 0];
        if (time == null) return null;
        return EventDetailInfoViewController.withTime(day, time);
    }

    // New begin date-time; the end follows when it would be before it. refreshUI=false while the
    // user is still typing in the field (rewriting its text would fight the keystrokes).
    private setBeginDateTime(begin:Date, refreshUI:boolean = true){
        this._event.beginDateTime = begin;

        if (this._event.endDateTime == null || this._event.endDateTime < begin) {
            let end = new Date(begin);
            end.setHours(end.getHours() + 4);
            this._event.endDateTime = end;
            this.endDateTextField.text = this.dateTimeString(end);
        }
        if (refreshUI) this.updateDateUI();
    }

    // On leaving a field: an unparsable value is reported once and the field shows the stored value
    private validateDateTimeField(textField:MUITextField, stored:Date){
        if (this.parseDateTime(textField.text) == null && (textField.text ?? "").trim().length > 0) {
            AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("INVALID_DATE_TIME", "INVALID DATE (DD/MM/YYYY HH:MM)"));
        }
        textField.text = this.dateTimeString(stored);
    }

    // Calendar pickers change the day and keep the time
    private setBeginDate(day:Date){
        this.setBeginDateTime(EventDetailInfoViewController.withDay(this._event.beginDateTime ?? day, day));
    }

    private setEndDate(day:Date){
        this._event.endDateTime = EventDetailInfoViewController.withDay(this._event.endDateTime ?? this._event.beginDateTime ?? day, day);
        this.updateDateUI();
    }

    private _datePickerTarget:"begin"|"end" = "begin";
    private showDatePicker(button:MUIButton, target:"begin"|"end"){
        this._datePickerTarget = target;

        let dp = new MUIDatePickerController();
        dp.init();
        dp.delegate = this;
        dp.modalPresentationStyle = MUIModalPresentationStyle.Popover;

        let pc = dp.popoverPresentationController;
        pc.sourceRect = button.frame;
        pc.sourceView = button;

        this.presentViewController(dp, true);
    }

    didSelectDate(datePickerController, date){
        if (this._datePickerTarget == "end") this.setEndDate(date);
        else this.setBeginDate(date);
    }

    // "HH:MM" -> [hours, minutes] or null
    private static parseTime(value:string):number[]{
        let m = /^\s*(\d{1,2})[:.hH]?(\d{2})?\s*$/.exec(value ?? "");
        if (m == null) return null;
        let h = parseInt(m[1]), min = m[2] != null ? parseInt(m[2]) : 0;
        if (h > 23 || min > 59) return null;
        return [h, min];
    }

    private static withTime(date:Date, time:number[]):Date{
        let d = new Date(date);
        d.setHours(time[0], time[1], 0, 0);
        return d;
    }

    private static withDay(date:Date, day:Date):Date{
        let d = new Date(date);
        d.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
        return d;
    }
}